import { S_IFIFO } from 'constants';
import * as React from 'react';
import { connect } from 'react-redux';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';

import {
  readActivitiesRecent,
  successReadActivitiesRecent
} from './actions/activity';
import { readBoardsDiff } from './actions/board';
import { readGroupsAll, readGroupsRelated } from './actions/group';
import { readMembersAll, rememberSigninMember } from './actions/member';
import { readMemosDiff } from './actions/memo';
import { readNotesDiff } from './actions/note';
import { readWikisDiff } from './actions/wiki';
import RelayCommunicator from './apis/RelayCommunicator';
import IndexedDB from './common/IndexedDB';
import Account from './components/Account';
import AccountCreate from './components/Account/create';
import AccountPassword from './components/Account/password';
import AccountPicture from './components/Account/picture';
import AccountSignin from './components/Account/signin';
import AdminGroups from './components/Admin/groups';
import AdminMembers from './components/Admin/members';
import Board from './components/Board';
import ErrorMessage from './components/ErrorMessage';
import ErrorRouting from './components/ErrorRouting';
import Loading from './components/Loading';
import Memo from './components/Memo';
import MemoByDeadline from './components/Memo/deadline';
import MemoGroup from './components/Memo/group';
import MemoReference from './components/Memo/reference';
import Note from './components/Note';
import Search from './components/Search';
import SettingsFunctions from './components/Settings/functions';
import SettingsMembers from './components/Settings/members';
import SettingsStatistics from './components/Settings/statistics';
import Wiki from './components/Wiki';
import WikiCreate from './components/Wiki/create';
import WikiHistory from './components/Wiki/history';
import WikiShow from './components/Wiki/show';
import WikiUpdate from './components/Wiki/update';
import Activity from './entities/Activity';
import Group from './entities/Group';
import Member from './entities/Member';

interface IProps {
  t?: any;
  account: Member;
  members: Member[];
  groups: Group[];
  rememberSigninMember: (member: Member) => {};
  readGroupsRelated: () => void;
  readMembersAll: () => void;
  readGroupsAll: () => void;
  readActivitiesRecent: () => void;
  socketActivity: (activity: Activity) => void;
  readMemosDiff: () => void;
  readWikisDiff: () => void;
  readNotesDiff: () => void;
  readBoardsDiff: () => void;
}

interface IState {
  checkingLoggedIn: boolean;
  loggedIn: boolean;
  initialized: boolean;
  accountReloaded: boolean;
}

class AppComponent extends React.Component<IProps, IState> {
  constructor(props: any) {
    super(props);
    this.state = {
      checkingLoggedIn: true,
      loggedIn: false,
      initialized: false,
      accountReloaded: false
    };
  }

  componentWillMount() {
    const isSignup = this.pathname().indexOf('member/signup') > -1;

    if (isSignup) {
      this.setState({
        checkingLoggedIn: false
      });
      return;
    }

    const db = new IndexedDB();
    db.get('login', 'auth').then(member => {
      if (member !== undefined) {
        this.props.rememberSigninMember(member);
        this.setState({
          loggedIn: true,
          checkingLoggedIn: false
        });
      } else {
        this.setState({
          checkingLoggedIn: false
        });
      }
    });
  }

  componentDidUpdate() {
    if (this.props.account.id !== undefined && !this.state.initialized) {
      this.setState({
        initialized: true
      });
      // this.socketStart();

      this.props.readMembersAll();

      if (this.props.account.isSystemAdmin) {
        this.props.readGroupsAll();
      } else {
        this.props.readGroupsRelated();
      }
      this.props.readActivitiesRecent();
    }
  }

  render() {
    if (this.state.checkingLoggedIn) {
      return null;
    }

    return (
      <BrowserRouter>
        <div id="App">
          {this.redirectSignin()}
          {this.checkAuth()}

          <Switch>
            <Route exact={true} path="/" component={Memo} />
            <Route path="/member/signin" component={AccountSignin} />
            <Route path="/member/signup/:token" component={AccountCreate} />
            <Route path="/settings/account" component={Account} />
            <Route path="/settings/password" component={AccountPassword} />
            <Route path="/settings/picture" component={AccountPicture} />
            <Route path="/members/create/:token" component={AccountCreate} />
            <Route path="/admin/members" component={AdminMembers} />
            <Route path="/admin/groups" component={AdminGroups} />
            <Route
              path="/:group/memos/reference/:memoId"
              component={MemoReference}
            />
            <Route path="/:group/memos" component={MemoGroup} />
            <Route exact={true} path="/:group/wikis" component={Wiki} />
            <Route exact={true} path="/:group/boards" component={Board} />
            <Route
              exact={true}
              path="/:group/wikis/create"
              component={WikiCreate}
            />
            <Route
              path="/:group/settings/functions"
              component={SettingsFunctions}
            />
            <Route
              path="/:group/settings/members"
              component={SettingsMembers}
            />
            <Route
              path="/:group/settings/statistics"
              component={SettingsStatistics}
            />
            <Route
              exact={true}
              path="/:group/wiki/:wikiId/update"
              component={WikiUpdate}
            />
            <Route
              exact={true}
              path="/:group/wiki/:wikiId/history"
              component={WikiHistory}
            />
            <Route path="/:group/wiki/:wikiId" component={WikiShow} />

            <Route path="/:group/notes" component={Note} />
            <Route path="/search/:keyword" component={Search} />
            <Route path="/search" component={Search} />
            <Route path="/deadline" component={MemoByDeadline} />

            <Route path="/" component={ErrorRouting} />
          </Switch>

          <Loading />
          <ErrorMessage />
        </div>
      </BrowserRouter>
    );
  }

  pathname(): string {
    return location.pathname
      .split('/')
      .slice(1)
      .join('/');
  }

  checkAuth() {
    if (
      !this.state.checkingLoggedIn &&
      this.state.loggedIn &&
      ['', 'member/signin', 'member/signup', 'index.html'].indexOf(
        this.pathname()
      ) > -1 &&
      this.pathname() !== '/'
    ) {
      return (
        <Redirect
          to={{
            pathname: '/',
            state: { from: location.pathname }
          }}
        />
      );
    } else {
      return null;
    }
  }

  isPublicUrl(): boolean {
    const publicUrls = [
      'member/signin',
      'member/signup',
      'member/resetPasswordEmail',
      'member/updatePassword'
    ];

    for (const url of publicUrls) {
      if (this.pathname().indexOf(url) > -1) {
        return true;
      }
    }
    return false;
  }

  redirectSignin() {
    if (this.props.account.id === undefined && !this.isPublicUrl()) {
      return (
        <Redirect
          to={{
            pathname: '/member/signin',
            state: { from: location.pathname }
          }}
        />
      );
    } else {
      return null;
    }
  }

  socketStart() {
    RelayCommunicator.onLoadApp({
      onConnected: () => {
        this.props.readMemosDiff();
        this.props.readWikisDiff();
        this.props.readNotesDiff();
        this.props.readBoardsDiff();
      },
      onActivity: (activity: Activity) => {
        console.log('onActivity', activity);
        this.props.socketActivity(activity);
        if (activity.memberId !== this.props.account.id) {
          if (activity.type === 'memo') {
            this.props.readMemosDiff();
          } else if (activity.type === 'wiki') {
            this.props.readWikisDiff();
          } else if (activity.type === 'note') {
            this.props.readNotesDiff();
          } else if (activity.type === 'board') {
            this.props.readBoardsDiff();
          }
        }
      }
    });
  }
}

const mapStateToProps = (state: any) => ({
  account: state.Account,
  members: state.Member,
  groups: state.Group
});

const mapDispatchToProps = (dispatch: any) => {
  return {
    rememberSigninMember(member: Member) {
      dispatch(rememberSigninMember(member));
    },
    readGroupsRelated() {
      dispatch(readGroupsRelated());
    },
    readGroupsAll() {
      dispatch(readGroupsAll());
    },
    readMembersAll() {
      dispatch(readMembersAll());
    },
    readActivitiesRecent() {
      dispatch(readActivitiesRecent());
    },
    socketActivity(activity: Activity) {
      dispatch(successReadActivitiesRecent([activity]));
    },
    readMemosDiff() {
      dispatch(readMemosDiff());
    },
    readWikisDiff() {
      dispatch(readWikisDiff());
    },
    readNotesDiff() {
      dispatch(readNotesDiff());
    },
    readBoardsDiff() {
      dispatch(readBoardsDiff());
    }
  };
};

const App = connect(
  mapStateToProps,
  mapDispatchToProps
)(AppComponent);

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx